import React from 'react';
import Dropzone from 'react-dropzone'; // Docs: https://react-dropzone.js.org/
import classNameBind from 'classnames/bind';
import { withTranslation } from '../Translator';
import Icon from '../Icon';
import Button from '../Button';
import Text from '../Text';
import Stack from '../Stack';
import Spinner from '../Spinner';
import Image from '../Image';
import Caption from '../Caption/Caption';
import Information from '../Information';
import styles from './FilePicker.module.css';
import Tooltip from '../Tooltip';

const bindClasses = classNameBind.bind(styles);

const FilePickerHorizontalPreview = ({ fileDetails, onCancelOrRemoveClick, isLoading }) => {
  const previewType = fileDetails.preview.type;
  const getFilePreview = () => {
    if (isLoading) return <Spinner className="small" />;
    if (previewType === 'image') return <Image src={fileDetails.preview.data} />;
    if (previewType === 'icon') return <Icon size="medium" color="blue" name={fileDetails.preview.data} />;

    return null;
  };

  return (
    <>
      <Stack.Item>
        <div className={bindClasses('file-preview', previewType)}>
          {getFilePreview()}
        </div>
      </Stack.Item>
      <Stack.Item fill>
        <Stack spacing="extraNarrow" vertical>
          <Stack.Item>
            <Tooltip position="top" content={fileDetails.text}>
              <Text cutText>{`${fileDetails.text}`}</Text>
            </Tooltip>
          </Stack.Item>
          <Stack.Item fill>
            <Caption subdued>
              {fileDetails.additionalInfo}
            </Caption>
          </Stack.Item>
        </Stack>
      </Stack.Item>
      <Stack.Item>
        <Button appearance="subdued" icon="cross" onClick={onCancelOrRemoveClick} />
      </Stack.Item>
    </>
  );
};

const FilePickerHorizontal = ({
  disabled,
  onCancelOrRemoveClick,
  fileDetails,
  isLoading,
  open,
  t,
}) => (
  <Stack
    fullWidth
    fullHeight
    spacing="narrow"
    horizontalAlignment="middle"
    verticalAlignment="center"
  >
    {fileDetails !== null ? (
      <FilePickerHorizontalPreview
        isLoading={isLoading}
        fileDetails={fileDetails}
        onCancelOrRemoveClick={onCancelOrRemoveClick}
      />
    ) : (
      <>
        <Stack.Item>
          <Button
            onClick={() => { open(); }}
            disabled={disabled}
            className={disabled && bindClasses('reset-opacity')}
            text={t('file-picker.button-add')}
            dataId="FilePicker.buttonAdd"
            appearance="simple"
            icon="plus"
          />
        </Stack.Item>
        <Stack.Item>
          <Text type="subdued">
            {t('file-picker.label-drop')}
          </Text>
        </Stack.Item>
      </>
    )}
  </Stack>
);

const FilePickerVertical = ({
  disabled,
  isLoading,
  onCancelOrRemoveClick,
  open,
  t,
}) => (
  <Stack
    vertical
    fullHeight
    spacing="narrow"
    horizontalAlignment="middle"
    verticalAlignment="center"
  >
    <Stack.Item>
      {isLoading ? <div className={bindClasses('loader')}><Spinner noMargin /></div> : (
        <Icon
          size="large"
          name="add-file"
          color="blue"
        />
      )}
    </Stack.Item>
    {isLoading && (
      <Stack.Item>
        <Text type="subdued">{t('file-picker.loading.text')}</Text>
      </Stack.Item>
    )}
    <Stack.Item>
      <Button
        onClick={() => (isLoading ? onCancelOrRemoveClick() : open())}
        disabled={disabled}
        className={disabled && bindClasses('reset-opacity')}
        text={t(isLoading ? 'file-picker.cancel.button.text' : 'file-picker.button-add')}
        dataId="FilePicker.buttonAdd"
        appearance={isLoading ? 'default' : 'simple'}
      />
    </Stack.Item>
    {!isLoading && (
      <Stack.Item>
        <Text type="subdued">
          {t('file-picker.label-drop')}
        </Text>
      </Stack.Item>
    )}
  </Stack>
);

const FilePicker = (props) => {
  const {
    onDrop,
    disabled,
    accept,
    maxFileSize,
    isLoading,
    type,
    fileDetails,
    error,
  } = props;

  return (
    <div className={bindClasses('wrapper')}>
      <Dropzone
        onDrop={onDrop}
        disabled={disabled}
        accept={accept}
        maxSize={maxFileSize}
        onClick={e => e.preventDefault()}
      >
        {({
          open,
          getRootProps,
          isDragActive,
          getInputProps,
        }) => (
          <div
            {...getRootProps()}
            className={bindClasses('dropzone', type, { active: isDragActive, isLoading, disabled, fileDetails: fileDetails !== null })}
            data-ppuc-id="FilePicker"
          >
            {type === 'horizontal' && <FilePickerHorizontal open={open} {...props} />}
            {type === 'vertical' && <FilePickerVertical open={open} {...props} />}
            <input {...getInputProps()} data-ppuc-id="FilePickerInput" />
          </div>
        )}
      </Dropzone>
      {error && (
        <div className={bindClasses('error')}>
          <Information type="error">{error}</Information>
        </div>
      )}
    </div>
  );
};

FilePicker.defaultProps = {
  type: 'vertical',
  disabled: false,
  isLoading: false,
  error: undefined,
  accept: undefined,
  maxFileSize: Infinity,
  onCancelOrRemoveClick: () => {},
  fileDetails: null,
};

const enhanced = withTranslation('components')(FilePicker);
enhanced.displayName = 'FilePicker';

export default enhanced;
export { FilePicker };
